import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import PreviewPage, { DEMO_DATA } from "../components/PreviewPage";
import { hostMCP, resolveHostedEndpoint } from "../api";
import { requireSignedIn } from "../lib/requireSignedIn";
import { fetchSessionUser } from "../lib/sessionUser";
import AuthConfigStep, {
  credentialsComplete,
  detectAuthType,
  type AuthScheme,
  type Credentials,
} from "../components/AuthConfigStep";
import BaseUrlConfigStep from "../components/BaseUrlConfigStep";
import AuthOverrideStep, {
  overrideToScheme,
  type OverrideMode,
} from "../components/AuthOverrideStep";
import OAuthConnectButton, {
  extractOAuthConfig,
  type OAuthProviderConfig,
} from "../components/OAuthConnectButton";

type RawPreviewResponse = {
  detected_format?: string;
  zip_b64?: string;
  report?: {
    summary?: {
      schema_warnings_suppressed?: number;
      schema_warnings_low?: number;
      schema_warnings_medium?: number;
    };
  };
  spec?: {
    base_url?: string;
    auth_schemes?: Array<{ type?: string; name?: string; scheme?: string; is_complex?: boolean }>;
  };
  endpoints?: Array<{
    method?: string;
    path?: string;
    summary?: string;
    support_level?: string;
    issues?: Array<{ type?: string; severity?: string; message?: string }>;
  }>;
};

function formatAutoFixMessage(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("operation_id") && m.includes("synthesized")) {
    return "generated operation id";
  }
  if (m.includes("description") && m.includes("auto-generated")) {
    return "generated missing description";
  }
  if (m.includes("defaulted to string") && m.includes("param")) {
    return "missing param type -> string";
  }
  if (m.includes("method missing") && m.includes("defaulted to get")) {
    return "missing method -> GET";
  }
  if (m.includes("inferred auth") || m.includes("auth") && m.includes("inferred")) {
    return "inferred auth settings";
  }
  return message;
}

function toPreviewPageData(raw?: RawPreviewResponse) {
  if (!raw || !Array.isArray(raw.endpoints)) return DEMO_DATA;

  const endpoints = raw.endpoints.map((ep) => {
    const issue   = ep.issues?.[0]?.message || "";
    const support = ep.support_level || "full";
    const status  =
      support === "manual" ? "error" : support === "partial" ? "warning" : "ok";
    const autoFixes = (ep.issues || [])
      .filter((i) => i.type === "missing_field" || /synthesized|auto-generated|defaulted|inferred/i.test(i.message || ""))
      .map((i) => formatAutoFixMessage(i.message || ""))
      .slice(0, 3);

    return {
      method:      ep.method  || "GET",
      path:        ep.path    || "/",
      description: ep.summary || `${ep.method || "GET"} ${ep.path || "/"}`,
      status,
      issue,
      autoFixes,
    };
  });

  const firstAuth = raw.spec?.auth_schemes?.[0];
  const summary   = raw.report?.summary;

  return {
    format:   raw.detected_format   || "Detected API",
    base_url: raw.spec?.base_url    || "",
    auth: firstAuth
      ? { type: firstAuth.type || firstAuth.scheme || "unknown", header: firstAuth.name || "-" }
      : { type: "none", header: "-" },
    endpoints,
    schema_warning_counts: {
      suppressed: summary?.schema_warnings_suppressed ?? 0,
      low:        summary?.schema_warnings_low        ?? 0,
      medium:     summary?.schema_warnings_medium     ?? 0,
    },
  };
}

export default function Preview() {
  const location     = useLocation();
  const navigate     = useNavigate();
  const [error, setError] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [baseUrlOverride, setBaseUrlOverride] = useState("");
  const [credentials, setCredentials] = useState<Credentials>({});
  const [credentialsSkipped, setCredentialsSkipped] = useState(false);
  const [authOverride, setAuthOverride] = useState<OverrideMode | null>(null);
  // OAuth state — whether the user has connected via OAuth
  const [oauthConnected, setOauthConnected] = useState(false);
  // When OAuth is detected, lets the user choose "Use API key instead" so the
  // credential box appears alongside the OAuth button rather than replacing it.
  const [useApiKeyInsteadOfOAuth, setUseApiKeyInsteadOfOAuth] = useState(false);

  const uploadedFile = (location.state as { uploadedFile?: File } | undefined)
    ?.uploadedFile;

  const parsedRawData = location.state?.parsedData as
    | RawPreviewResponse
    | undefined;

  const parsedData = useMemo(
    () => toPreviewPageData(parsedRawData),
    [parsedRawData]
  );

  // Pull auth schemes off the backend preview response.
  const authSchemes: AuthScheme[] = useMemo(() => {
    const raw = parsedRawData?.spec?.auth_schemes;
    if (!Array.isArray(raw)) return [];
    return raw.map(s => ({
      type: s.type || "",
      scheme: s.scheme,
      name: s.name,
      is_complex: s.is_complex,
      authorization_url: (s as { authorization_url?: string | null }).authorization_url,
      token_url: (s as { token_url?: string | null }).token_url,
      scopes: (s as { scopes?: string[] }).scopes,
    }));
  }, [parsedRawData]);

  // Usable = anything we can render a real input for (basic/bearer/apiKey).
  // oauth2 / openIdConnect schemes are filtered out so the manual override
  // UI can take over.
  const usableSchemesFromSpec: AuthScheme[] = useMemo(() => {
    const usable = authSchemes.filter(s => s.type === "http" || s.type === "apiKey");
    if (usable.length > 0) return usable;

    const displayAuthType = (parsedData.auth?.type || "").toLowerCase();
    const displayAuthScheme = ((parsedData.auth as { scheme?: string })?.scheme || "").toLowerCase();
    if (displayAuthType === "http") return [{ type: "http", scheme: displayAuthScheme || "bearer" }];
    if (displayAuthType === "bearer") return [{ type: "http", scheme: "bearer" }];
    if (displayAuthType === "basic") return [{ type: "http", scheme: "basic" }];
    if (displayAuthType === "apikey") return [{ type: "apiKey" }];

    return [];
  }, [authSchemes, parsedData.auth]);

  const onlyComplexAuth =
    authSchemes.length > 0 &&
    authSchemes.every(s => s.type === "oauth2" || s.type === "openIdConnect");

  // ── OAuth config — extracted from the spec's oauth2/openIdConnect schemes ──
  // If the spec declares OAuth, we try to extract the authorization + token URLs.
  // This powers the OAuthConnectButton which shows FIRST before any credential box.
  const oauthConfig: OAuthProviderConfig | null = useMemo(() => {
    const hasOauthScheme = authSchemes.some(
      s => s.type === "oauth2" || s.type === "openIdConnect",
    );
    if (!hasOauthScheme) return null;
    return extractOAuthConfig(authSchemes);
  }, [authSchemes]);

  // Whether this API supports OAuth AND we have enough config to show the button.
  // Some OAuth specs embed the authorize/token URLs in the scheme; others don't.
  // If we can extract config → show OAuth button. If not → show the manual override.
  const hasOAuthSupport = oauthConfig !== null && !!(oauthConfig.authorizeUrl && oauthConfig.tokenUrl);

  // If spec only has OAuth AND we have OAuth support → don't need the manual override
  // If spec only has OAuth BUT we can't extract URLs → fall through to manual override
  const needsManualOverride = usableSchemesFromSpec.length === 0 && !hasOAuthSupport;
  const overrideReason: "oauth2" | "missing" = onlyComplexAuth ? "oauth2" : "missing";

  const authSchemesForSetup: AuthScheme[] = useMemo(() => {
    if (usableSchemesFromSpec.length > 0) return usableSchemesFromSpec;
    if (authOverride) return overrideToScheme(authOverride);
    // OAuth-only spec where the user opted for "Use API key instead" — fall back
    // to a bearer-token input (most common for APIs that also support OAuth).
    if (hasOAuthSupport && useApiKeyInsteadOfOAuth) return [{ type: "http", scheme: "bearer" }];
    return [];
  }, [usableSchemesFromSpec, authOverride, hasOAuthSupport, useApiKeyInsteadOfOAuth]);

  // If the spec's base_url looks like a placeholder, the user needs to
  // supply a real one before we can generate.
  const showBaseUrlInput = useMemo(() => {
    const url = parsedData.base_url || "";
    return !url || /\{|example\.com|your-?company|your-?domain|placeholder/i.test(url);
  }, [parsedData.base_url]);

  const baseUrlReady = !showBaseUrlInput || !!baseUrlOverride.trim();
  const authReady =
    // OAuth was completed via the OAuthConnectButton
    oauthConnected ||
    credentialsSkipped ||
    authOverride === "none" ||
    (authSchemesForSetup.length > 0 && credentialsComplete(authSchemesForSetup, credentials)) ||
    // Final fallback: nothing to configure (no OAuth offered, no usable schemes,
    // no manual override needed). For OAuth-detected specs the user must pick
    // an explicit path (connect, use API key, or skip).
    (!hasOAuthSupport && authSchemesForSetup.length === 0 && !needsManualOverride);
  const canSubmitSetup = baseUrlReady && authReady;

  async function handleGenerate() {
    await requireSignedIn(async () => {
    if (!uploadedFile) {
      setError("No file found. Please go back and upload again.");
      return;
    }
    if (!authReady) {
      setError(
        hasOAuthSupport && !oauthConnected
          ? "Connect your OAuth account above before generating."
          : needsManualOverride && !authOverride
          ? "Pick an authentication method above before generating."
          : "Fill in your API credentials above before generating.",
      );
      return;
    }
    try {
      setError("");
      setIsConverting(true);

      // Strip empty fields from credentials so the backend doesn't store noise.
      // When the user picked "no auth needed", credentials is {} and we
      // explicitly send null so the backend skips credential encryption.
      const credsToSend = credentialsSkipped
        ? ({} as Record<string, string>)
        : (Object.fromEntries(
            Object.entries(credentials).filter(([, v]) => typeof v === "string" && v.trim() !== ""),
          ) as Record<string, string>);
      const hasCreds = !credentialsSkipped && Object.keys(credsToSend).length > 0;

      const hostUser = await fetchSessionUser();
      const hostEmail = hostUser?.email?.trim();
      if (!hostEmail) {
        setError("Sign in is required to save your MCP to your dashboard.");
        return;
      }

      const hostData = await hostMCP(uploadedFile, hostEmail, {
        credentials: hasCreds ? credsToSend : null,
        baseUrlOverride: baseUrlOverride.trim() || null,
      }) as {
        ok?: boolean;
        mcp_url?: string;
        sse_url?: string;
        server_id?: string;
        spec?: { title?: string; base_url?: string };
      };

      const id = hostData.server_id?.trim();
      const mcpPath = hostData.mcp_url?.trim() || (id ? `/mcp/${id}` : "");
      const ssePath = hostData.sse_url?.trim() || (id ? `/mcp/${id}/sse` : "");
      const mcpUrlAbs = mcpPath ? resolveHostedEndpoint(mcpPath) : "";
      const sseUrlAbs = ssePath ? resolveHostedEndpoint(ssePath) : "";

      navigate("/connect", {
        state: {
          specTitle:  hostData.spec?.title    || parsedData.format   || "my-api",
          baseUrl:    baseUrlOverride.trim() || hostData.spec?.base_url || parsedData.base_url || "",
          serverPath: "server.py",
          mcpUrl: mcpUrlAbs,
          sseUrl: sseUrlAbs,
        }
      });

    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Hosting failed. Please try again.");
    } finally {
      setIsConverting(false);
    }
    });
  }

  return (
    <main className="min-h-screen bg-gray-50 py-10">
      {error && (
        <p className="text-center text-red-500 text-sm mb-4">{error}</p>
      )}

      {/* ── Auth / base URL setup card ────────────────────────────────────── */}
      {/*
        Priority logic (top to bottom, first match wins):
        1. OAuth button — shown when spec declares oauth2/openIdConnect AND
           we can extract authorize+token URLs from the spec.
        2. Manual override picker — shown when spec has ONLY OAuth/missing auth
           BUT we couldn't extract OAuth URLs (user picks bearer/basic/apiKey).
        3. Credential fields — shown for bearer/basic/apiKey specs as always.
        4. Base URL input — shown when the spec has a placeholder base URL.

        Rules:
        - We NEVER remove or modify the credential fields (AuthConfigStep).
        - OAuth is simply added ABOVE and disables the credential requirement
          if connected. If the API doesn't use OAuth, the button is never shown.
      */}
      {(showBaseUrlInput ||
        hasOAuthSupport ||
        needsManualOverride ||
        (authSchemesForSetup.length > 0 && detectAuthType(authSchemesForSetup) !== "none")) && (
        <div className="mt-4" style={{ maxWidth: 680, margin: "0 auto", padding: "0 16px" }}>
          <div className="glass-strong rounded-2xl p-7 shadow-soft w-full">

              {/* 1. Base URL override (unchanged) */}
              {showBaseUrlInput ? (
                <BaseUrlConfigStep
                  embedded
                  specBaseUrl={parsedData.base_url}
                  value={baseUrlOverride}
                  onChange={setBaseUrlOverride}
                />
              ) : null}

              {/* 2. OAuth Connect Button — shown when spec has OAuth AND we have URLs */}
              {hasOAuthSupport && oauthConfig ? (
                <div className={showBaseUrlInput ? "pt-6 border-t border-border" : undefined}>
                  <OAuthConnectButton
                    config={oauthConfig}
                    isConnected={oauthConnected}
                    onSuccess={(encryptedToken: string) => {
                      // Store the encrypted token in credentials dict
                      // It will be sent to /api/v1/host as credentials_json
                      setCredentials(prev => ({
                        ...prev,
                        oauth_access_token_enc: encryptedToken,
                      }));
                      setOauthConnected(true);
                      setCredentialsSkipped(false);
                      setUseApiKeyInsteadOfOAuth(false);
                    }}
                    onDisconnect={() => {
                      setOauthConnected(false);
                      setCredentials(prev => {
                        const next = { ...prev };
                        delete next.oauth_access_token_enc;
                        return next;
                      });
                    }}
                  />

                  {/* Alternative paths — only while the user hasn't picked one. */}
                  {!oauthConnected && !credentialsSkipped && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
                      <span className="text-muted-foreground">or</span>
                      <button
                        type="button"
                        onClick={() => setUseApiKeyInsteadOfOAuth(v => !v)}
                        className="font-medium text-foreground hover:underline underline-offset-2"
                      >
                        {useApiKeyInsteadOfOAuth ? "Hide API key field" : "Use API key instead"}
                      </button>
                      <span className="text-muted-foreground">·</span>
                      <button
                        type="button"
                        onClick={() => {
                          setCredentialsSkipped(true);
                          setUseApiKeyInsteadOfOAuth(false);
                          setCredentials({});
                        }}
                        className="font-medium text-muted-foreground hover:text-foreground"
                      >
                        Skip — add credentials later
                      </button>
                    </div>
                  )}

                  {!oauthConnected && credentialsSkipped && (
                    <div className="mt-3 flex items-center justify-between gap-3 text-[11px]">
                      <span className="text-muted-foreground">
                        Skipped — you can add credentials later from your dashboard.
                      </span>
                      <button
                        type="button"
                        onClick={() => setCredentialsSkipped(false)}
                        className="font-medium text-foreground hover:underline underline-offset-2 shrink-0"
                      >
                        Add credentials
                      </button>
                    </div>
                  )}
                </div>
              ) : null}

              {/* 3. Manual override picker — only when OAuth unavailable AND spec has no static auth */}
              {needsManualOverride ? (
                <AuthOverrideStep
                  embedded
                  withTopDivider={showBaseUrlInput}
                  reason={overrideReason}
                  value={authOverride}
                  onChange={mode => {
                    setAuthOverride(mode);
                    setCredentials({});
                    setCredentialsSkipped(false);
                  }}
                />
              ) : null}

              {/* 4. Credential fields (AuthConfigStep)
                  Shown when:
                  a) The spec has static auth (bearer/basic/apiKey), OR
                  b) The user picked an override mode for an OAuth/missing spec, OR
                  c) The spec has OAuth and the user clicked "Use API key instead"
                  Suppressed when OAuth is available and the user hasn't opted into
                  the API key path (and isn't already in the skipped state — the
                  skipped indicator shows in the OAuth section instead). */}
              {(!hasOAuthSupport || useApiKeyInsteadOfOAuth)
                && authSchemesForSetup.length > 0
                && detectAuthType(authSchemesForSetup) !== "none"
                && !(hasOAuthSupport && credentialsSkipped) ? (
                <AuthConfigStep
                  embedded
                  withTopDivider={showBaseUrlInput || needsManualOverride || hasOAuthSupport}
                  authSchemes={authSchemesForSetup}
                  credentials={credentials}
                  onChange={setCredentials}
                  skipped={credentialsSkipped}
                  onSkip={() => {
                    setCredentialsSkipped(true);
                    setCredentials({});
                    if (hasOAuthSupport) setUseApiKeyInsteadOfOAuth(false);
                  }}
                  onResume={() => setCredentialsSkipped(false)}
                />
              ) : null}
            </div>
        </div>
      )}

      {/* ── Generate card (matches glass-strong styling) ── */}
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut", delay: 0.05 }}
        className="mt-4"
        style={{ maxWidth: 680, margin: "0 auto", padding: "0 16px" }}
      >
          <button
              type="button"
              onClick={handleGenerate}
              disabled={!canSubmitSetup || isConverting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold text-white bg-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-soft"
            >
              <Sparkles className="w-4 h-4" />
              {isConverting ? "Generating…" : "Generate & host MCP server"}
            </button>
            {!canSubmitSetup && !isConverting && (
              <p className="text-[11px] text-muted-foreground text-center mt-2.5">
                {hasOAuthSupport && !oauthConnected
                  ? "Connect your OAuth account above to continue"
                  : needsManualOverride && !authOverride
                  ? "Pick an authentication method above to continue"
                  : "Fill in the fields above to enable Generate"}
              </p>
            )}
            {!credentialsSkipped && !oauthConnected && (
              <p className="text-[11px] text-muted-foreground text-center mt-2">
                Credentials are encrypted at rest and never appear in your MCP link.
              </p>
            )}
            {oauthConnected && (
              <p className="text-[11px] text-muted-foreground text-center mt-2">
                OAuth token encrypted · never stored in plaintext · secured with PKCE
              </p>
            )}
      </motion.section>

      <PreviewPage
        parsedData={parsedData}
        onGenerate={handleGenerate}
        baseUrlOverride={baseUrlOverride}
        onBaseUrlOverrideChange={setBaseUrlOverride}
        needsBaseUrlInput={showBaseUrlInput}
        authSchemes={authSchemesForSetup as never[]}
        credentials={credentials}
        onCredentialsChange={setCredentials}
        canSubmitSetup={canSubmitSetup}
        isConverting={isConverting}
        hideInlineSetup
        showPostPreviewAuthSetup={false}
        authOverride={null}
        onAuthOverrideChange={() => {}}
      />
    </main>
  );
}
